import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import POSPage from "@/pages/POSPage";
import KitchenPage from "@/pages/KitchenPage";
import ReportsPage from "@/pages/ReportsPage";
import SettingsPage from "@/pages/SettingsPage";
import ReservationsPage from "@/pages/ReservationsPage";
import LoyaltyPage from "@/pages/LoyaltyPage";
import LocationsPage from "@/pages/LocationsPage";
import InventoryPage from "@/pages/InventoryPage";
import SelfOrderPage from "@/pages/SelfOrderPage";
import FeedbackPage from "@/pages/FeedbackPage";
import FeedbackDashboard from "@/pages/FeedbackDashboard";
import ShiftsPage from "@/pages/ShiftsPage";
import TimeclockPage from "@/pages/TimeclockPage";
import LaborCostsPage from "@/pages/LaborCostsPage";
import EquipmentPage from "@/pages/EquipmentPage";
import NetworkPage from "@/pages/NetworkPage";
import UsersPage from "@/pages/UsersPage";
import ClockPage from "@/pages/ClockPage";
import DriverPage from "@/pages/DriverPage";
import TrackDeliveryPage from "@/pages/TrackDeliveryPage";
import CustomersPage from "@/pages/CustomersPage";
import CustomerDisplayPage from "@/pages/CustomerDisplayPage";
import LoyaltySignupPage from "@/pages/LoyaltySignupPage";
import MerchantOnboardingMockup from "@/pages/MerchantOnboardingMockup";
import { ThemeProvider } from "@/components/ThemeToggle";
import IdleWatcher from "@/components/IdleWatcher";
import useSiteBranding from "@/hooks/useSiteBranding";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
// Token storage is deliberately different for the demo window: sessionStorage
// (dies on tab close) with a distinct key so the demo login can't leak into
// the main POS tab or vice-versa.
const _isDemoBoot = typeof window !== "undefined"
  && new URLSearchParams(window.location.search).get("demo") === "1";
const TOKEN_KEY = _isDemoBoot ? "pos_token_demo" : "pos_token";
const _tokenStore = _isDemoBoot ? sessionStorage : localStorage;

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(_tokenStore.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const validateToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error("Token validation failed:", error);
          _tokenStore.removeItem(TOKEN_KEY);
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    validateToken();
  }, [token]);

  const login = async (pin) => {
    try {
      const device = navigator.userAgent || "Unknown";
      const response = await axios.post(`${API}/auth/login`, { pin, device });
      const { token: newToken, user: userData } = response.data;
      _tokenStore.setItem(TOKEN_KEY, newToken);
      setToken(newToken);
      setUser(userData);
      // KDS terminals don't clock in (see backend auth.login) — showing
      // a "Clocked in!" toast for them contradicts reality and confuses
      // managers reviewing labor. Other roles get the standard clock-in
      // greeting; if the backend suppressed clock-in for a specific
      // reason it returns clock_in_id=null and we fall back to a plain
      // welcome.
      if (userData.role === "kds") {
        toast.success(`Welcome, ${userData.name}`);
      } else if (response.data.clock_in_id) {
        toast.success(`Clocked in! Welcome, ${userData.name}`);
      } else {
        toast.success(`Welcome, ${userData.name}`);
      }
      return { success: true, role: userData.role };
    } catch (error) {
      const message = error.response?.data?.detail || "Invalid PIN";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    // Note: logout does NOT clock the user out anymore.  Clock-in/out is a
    // separate accounting concern; staff must use the explicit "Clock Out"
    // button on the login screen (or the Time Clock page) when they actually
    // end their shift.  Auto-logout for inactivity would otherwise mangle
    // their recorded hours.
    _tokenStore.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={user.role === "kds" ? "/kitchen" : user.role === "driver" ? "/driver" : "/pos"} replace />;
  }

  return (
    <>
      {isDemoMode() && (
        <div
          className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-black text-xs font-medium py-1 px-3 flex items-center justify-center gap-2 shadow"
          data-testid="demo-mode-banner"
        >
          <span className="inline-block w-2 h-2 rounded-full bg-black animate-pulse"></span>
          DEMO / TRAINING MODE — this window is isolated from live service. Nothing here affects real orders, reports, or labor.
        </div>
      )}
      <div className={isDemoMode() ? "pt-6" : ""}>
        <IdleWatcher />
        {children}
      </div>
    </>
  );
};

// Demo-mode detection. The DemoModeCard opens `/pos?demo=1` in a fresh
// window; when that URL param is present we flip a global flag and every
// axios request tags itself with `X-Demo-Mode: 1` so the backend routes
// against the isolated demo database (see backend/core/config.py::_DBProxy).
// The token is scoped to sessionStorage so closing the demo window fully
// resets the demo login without touching the main POS session.
const IS_DEMO_WINDOW = typeof window !== "undefined"
  && new URLSearchParams(window.location.search).get("demo") === "1";
if (IS_DEMO_WINDOW) {
  window.__POS_DEMO_MODE__ = true;
  // Preserve `?demo=1` across in-app navigations by patching the History API
  // once at boot — cleaner than sprinkling `?demo=1` through every router
  // link.
  try {
    const origPush = window.history.pushState.bind(window.history);
    const origReplace = window.history.replaceState.bind(window.history);
    const stampDemo = (url) => {
      if (typeof url !== "string") return url;
      if (url.includes("demo=1")) return url;
      return url + (url.includes("?") ? "&" : "?") + "demo=1";
    };
    window.history.pushState = (s, t, url) => origPush(s, t, stampDemo(url));
    window.history.replaceState = (s, t, url) => origReplace(s, t, stampDemo(url));
  } catch (e) { /* non-critical */ }
}
export const isDemoMode = () => !!(typeof window !== "undefined" && window.__POS_DEMO_MODE__);

// API Interceptor setup
axios.interceptors.request.use((config) => {
  // Demo windows use a sessionStorage-scoped token so credentials never
  // leak between the main POS window and the demo window.
  const tokenKey = isDemoMode() ? "pos_token_demo" : "pos_token";
  const token = isDemoMode()
    ? sessionStorage.getItem(tokenKey)
    : localStorage.getItem(tokenKey);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (isDemoMode()) {
    config.headers["X-Demo-Mode"] = "1";
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const detail = error.response?.data?.detail || "";
      // Only nuke the session when the 401 actually says the session is
      // dead. Individual per-endpoint 401s (e.g. from a background probe
      // that hits a role check race) shouldn't log the cashier out —
      // that behaviour caused sporadic "kicked out on edit/delete user"
      // reports. Let the caller handle those via .catch().
      const isSessionDead =
        /expired|invalid token|user not found|another device|not authenticated/i.test(detail);
      if (isSessionDead) {
        _tokenStore.removeItem(TOKEN_KEY);
        if (detail.includes("another device")) {
          localStorage.setItem("pos_kicked", "You were signed out because your PIN was used on another device.");
        }
        window.location.href = isDemoMode() ? "/login?demo=1" : "/login";
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  // Live-sync the browser tab title + favicon with the operator's
  // configured branding. Runs once at mount; the Settings page bumps a
  // window event to force a re-fetch after edits.
  const [brandingVersion, setBrandingVersion] = useState(0);
  useSiteBranding(brandingVersion);
  useEffect(() => {
    const bump = () => setBrandingVersion((v) => v + 1);
    window.addEventListener("crest:branding-updated", bump);
    return () => window.removeEventListener("crest:branding-updated", bump);
  }, []);

  // Seed data on first load
  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (error) {
        // Data already seeded or error, ignore
      }
    };
    seedData();
  }, []);

  return (
    <AuthProvider>
      <ThemeProvider>
        <div className="App">
          <BrowserRouter>
            <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/pos"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager", "server", "cashier"]}>
                  <POSPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/kitchen"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager", "server", "cashier", "kds", "cook"]}>
                  <KitchenPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <ReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager", "server", "cashier"]}>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reservations"
              element={
                <ProtectedRoute>
                  <ReservationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/loyalty"
              element={
                <ProtectedRoute>
                  <LoyaltyPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/locations"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <LocationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <InventoryPage />
                </ProtectedRoute>
              }
            />
            <Route path="/order" element={<SelfOrderPage />} />
            <Route path="/track/:orderId" element={<TrackDeliveryPage />} />
            <Route
              path="/driver"
              element={
                <ProtectedRoute allowedRoles={["driver", "admin", "manager"]}>
                  <DriverPage />
                </ProtectedRoute>
              }
            />
            <Route path="/customer-display" element={<CustomerDisplayPage />} />
            <Route path="/loyalty-signup" element={<LoyaltySignupPage />} />
            <Route path="/merchant-onboarding" element={<MerchantOnboardingMockup />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route
              path="/feedback-dashboard"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <FeedbackDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/shifts"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <ShiftsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/timeclock"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <TimeclockPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/labor-costs"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <LaborCostsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/equipment"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <EquipmentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/network"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <NetworkPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clock"
              element={
                <ProtectedRoute>
                  <ClockPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <CustomersPage />
                </ProtectedRoute>
              }
            />
            {/* Legacy /pos-settings — merged into /settings → Equipment → POS.
                Keep the path alive but redirect so bookmarks don't 404. */}
            <Route
              path="/pos-settings"
              element={<Navigate to="/settings" replace />}
            />
            <Route path="/" element={<Navigate to="/order" replace />} />
            <Route path="*" element={<Navigate to="/order" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        </div>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
